import { computed, effect, Injectable, signal } from '@angular/core';
import { cloneDeep, sortBy } from 'lodash';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { CompanyIcpService } from 'src/app/components/company-icp/company-icp.service';

import { MatDialog } from '@angular/material/dialog';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { TemplateEditorService } from 'src/app/template-editor/services/template-editor.service';
import { AIChatbotInterpretationResponse } from '../../models/ai-chatbot-interpretation-response';
import {
  AIChatbotMessage,
  AIChatbotMessageContent,
} from '../../models/ai-chatbot-message';
import { AiChatbotTemplateTunerGeneration } from '../../models/ai-chatbot-presentation-data';
import { AIChatbotSession } from '../../models/ai-chatbot-session';
import { AIChatbotApiService } from '../ai-chatbot-api/ai-chatbot-api.service';

import { StateService } from '@uirouter/core';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';
import { BrandingService } from 'src/app/template-editor/template-components/services/branding.service';
import { AIChatbotConversationLoggingService } from '../ai-chatbot-conversation-logging/ai-chatbot-conversation-logging.service';

const SHOW_BRANDING_STORAGE_KEY = 'risevision.aichatbot.showBranding';

@Injectable({
  providedIn: 'root',
})
export class AIChatbotService {
  styles = [
    {
      name: 'Modern (clean, minimal, trendy)',
      value: 'modern',
    },
    {
      name: 'Classic (serif fonts, formal)',
      value: 'classic',
    },
    {
      name: 'Playful (bright, fun, kid friendly)',
      value: 'playful',
    },
    {
      name: 'Bold (high contrast)',
      value: 'bold',
    },
    {
      name: 'Vibrant (colorful)',
      value: 'vibrant',
    },
    {
      name: 'Professional (corporate, clean)',
      value: 'professional',
    },
    {
      name: 'Calm (pastel tones, relaxed)',
      value: 'calm',
    },
  ];

  contents = [
    {
      icon: 'component-video',
      name: 'Video',
      value: 'video',
    },
    {
      icon: 'component-video-streaming',
      name: 'Video Streaming',
      value: 'video-streaming',
    },
    {
      icon: 'component-playlist',
      name: 'Playlist',
      value: 'playlist',
    },
    {
      icon: 'component-date',
      name: 'Date',
      value: 'date',
    },
    {
      icon: 'component-counter',
      name: 'Counter',
      value: 'counter',
    },
    {
      icon: 'component-youtube',
      name: 'Youtube',
      value: 'youtube',
    },
    {
      icon: 'component-weather',
      name: 'Weather',
      value: 'weather',
    },
    {
      icon: 'component-rss',
      name: 'RSS News',
      value: 'rss',
    },
    {
      icon: 'component-screen-sharing',
      name: 'Screen Share Code',
      value: 'screen-sharing',
    },
    {
      icon: 'component-canva',
      name: 'Canva',
      value: 'canva',
    },
  ];

  static extractBrandingColors(
    html: string,
  ): { baseColor: string; accentColor: string } | null {
    const baseMatch = html.match(/--branding-color-base:\s*([^;]+)/);
    const accentMatch = html.match(/--branding-color-accent:\s*([^;]+)/);
    const baseColor = baseMatch?.[1]?.trim();
    const accentColor = accentMatch?.[1]?.trim();

    if (!baseColor || !accentColor) {
      return null;
    }

    return {
      baseColor,
      accentColor,
    };
  }

  industries = sortBy(
    CompanyIcpService.COMPANY_INDUSTRY_FIELDS.map((industry) => ({
      name: industry[0],
      value: industry[1],
      selected: false,
    })),
    (industry) => industry.name,
  );

  errorMessages = [
    'The task failed… successfully! 👍 Please try again.',
    '👀 Oops, something went wrong. Please try again.',
    '💥🤖💥 Sorry, I hit a little glitch. Please try again.',
    "🙈 I'm sorry... I got distracted. Try again?",
    '⛔️ Error! The developers dropped the ball on this one! Try again.',
    "⛔️ Error! The design team didn't design for this scenario. Try again.",
    "⚙️ Oh no! I tripped over my own gears. I'm rebooting — try again.",
    "🤔 Hmm, that didn't work as expected. Want to try again?",
    'I accidentally sent your request into space. Waiting for it to come back. Want to try again?',
  ];

  isProcessing = false;
  private _isLoadingTemplates = false;
  get isLoadingTemplates(): boolean {
    return this._isLoadingTemplates;
  }
  set isLoadingTemplates(value: boolean) {
    this._isLoadingTemplates = value;
    this.isLoadingTemplates$.next(value);

    if (value && !this.interpretationResponse) {
      this.showLoadingSamples$.next();
    }
  }
  isLoadingTemplates$ = new BehaviorSubject<boolean>(false);
  interpretationResponse?: AIChatbotInterpretationResponse;
  templatesResponse?: AIChatbotMessage['content'];

  showLoadingSamples$ = new Subject<void>();
  // SSE events for loading progress and partial template updates
  sseLoadingMessage$ = new Subject<{ data: string; message: string }>();
  ssePartialTemplates$ = new Subject<{
    partial: AiChatbotTemplateTunerGeneration;
    generationIndex: number;
  }>();
  scrollChatToBottom$ = new ReplaySubject<{ onlyIfAtBottom?: boolean }>(1);
  restartSession$ = new Subject<void>();

  private cancelRequest$ = new Subject<void>();

  currentSession = signal<AIChatbotSession | undefined>(undefined);

  readonly isMessagesEmpty = computed(
    () => (this.currentSession()?.messages() ?? []).length === 0,
  );

  selectedTemplateTunerPreview = signal<{
    messageId: string;
    optionIndex: number;
    presentation: AiChatbotTemplateTunerGeneration;
  } | null>(null);

  templateEditHistory: { html: string; userMessage: string }[] = [];
  private previewEditStateMap = new Map<
    string,
    { content: string; editHistory: { html: string; userMessage: string }[] }
  >();

  showBranding = signal<boolean>(false);

  selectedMode = signal<'fast' | 'quality'>('quality');

  constructor(
    private trackerService: TrackerService,
    private aiChatbotApiService: AIChatbotApiService,
    private dialog: MatDialog,
    private templateEditorService: TemplateEditorService,
    private conversationLoggingService: AIChatbotConversationLoggingService,
    private aiCreditsService: AiCreditsService,
    private stateService: StateService,
    private localStorageHelperService: LocalStorageHelperService,
    private brandingService: BrandingService,
  ) {
    this.showBranding.set(
      !!this.localStorageHelperService.get(SHOW_BRANDING_STORAGE_KEY),
    );

    // Prime brandingSettings before the user opens a template in the editor.
    this.brandingService.getBrandingComponent();

    effect(() => {
      this.localStorageHelperService.set(
        SHOW_BRANDING_STORAGE_KEY,
        this.showBranding(),
      );
    });
  }

  startNewSession(): AIChatbotSession {
    const sessionId = this.generateSessionId();
    const messagesSignal = signal<AIChatbotMessage[]>([]);

    // Clear logged messages cache when starting a new session
    this.conversationLoggingService.clearLoggedMessages();

    const session: AIChatbotSession = {
      id: sessionId,
      messages: messagesSignal,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.currentSession.set(session);

    this.trackerService.presentationEvent(
      'AI Chatbot Session Started',
      sessionId,
    );

    this.restartSession$.next();

    return session;
  }

  private updateMessages(
    messages: AIChatbotMessage[],
    loggingMeta?: { durationMs?: number; creditsUsed?: number },
  ): void {
    const session = this.currentSession();
    if (session) {
      const previousMessages = session.messages();
      const previousMessageIds = new Set(previousMessages.map((m) => m.id));

      // Find new messages that weren't in the previous array
      const newMessages = messages.filter(
        (msg) => !previousMessageIds.has(msg.id),
      );

      session.messages.set(messages);
      session.updatedAt = new Date();
      this.scrollChatToBottom$.next({});

      // Log new messages to BigQuery
      if (newMessages.length > 0) {
        const selectedItem = this.buildSelectedItem(messages);
        newMessages.forEach((message) => {
          const messageIdx = messages.findIndex((m) => m.id === message.id);
          if (messageIdx !== -1) {
            // Log asynchronously to not block UI
            this.conversationLoggingService
              .logMessage(
                session.id,
                messageIdx,
                message,
                selectedItem,
                loggingMeta?.durationMs,
                loggingMeta?.creditsUsed,
              )
              .subscribe({
                error: (error) => {
                  console.error('Error logging message to BigQuery:', error);
                },
              });
          }
        });
      }
    }
  }

  /**
   * Builds the "optionIndex" string describing the currently selected template tuner variation
   */
  private buildSelectedItem(messages?: AIChatbotMessage[]): string | null {
    const preview = this.selectedTemplateTunerPreview();
    if (!preview) {
      return null;
    }
    return String(preview.optionIndex);
  }

  setSelectedTemplateTunerPreview(
    messageId: string,
    optionIndex: number,
    presentation: AiChatbotTemplateTunerGeneration,
  ): void {
    const current = this.selectedTemplateTunerPreview();
    if (current) {
      const currentKey = `${current.messageId}-${current.optionIndex}`;
      this.previewEditStateMap.set(currentKey, {
        content: current.presentation.content ?? '',
        editHistory: [...this.templateEditHistory],
      });
    }

    const newKey = `${messageId}-${optionIndex}`;
    const savedState = this.previewEditStateMap.get(newKey);

    if (savedState) {
      this.templateEditHistory = [...savedState.editHistory];
      this.selectedTemplateTunerPreview.set({
        messageId,
        optionIndex,
        presentation: { ...presentation, content: savedState.content },
      });
    } else {
      this.templateEditHistory = [];
      this.selectedTemplateTunerPreview.set({
        messageId,
        optionIndex,
        presentation,
      });
    }
  }

  clearSelectedTemplateTunerPreview(): void {
    this.selectedTemplateTunerPreview.set(null);
    this.templateEditHistory = [];
    this.previewEditStateMap.clear();
  }

  private updateTemplateTunerPreviewContent(editedContent: string): void {
    const currentPreview = this.selectedTemplateTunerPreview();
    if (!currentPreview) {
      return;
    }

    this.selectedTemplateTunerPreview.set({
      ...currentPreview,
      presentation: {
        ...currentPreview.presentation,
        content: editedContent,
      },
    });
  }

  private handleTemplateTunerSelection(selectedIndex: number): void {
    const messages = this.currentSession()?.messages() ?? [];

    // Find the most recent template tuner message
    const tunerMessage = [...messages]
      .reverse()
      .find(
        (msg) =>
          msg.content?.type === 'template_tuner_create' ||
          msg.content?.type === 'template_tuner_retrieve',
      );

    const arrayIndex = selectedIndex - 1;
    const presentation = tunerMessage?.content?.data?.[0]?.[arrayIndex];

    if (tunerMessage && presentation) {
      this.setSelectedTemplateTunerPreview(
        tunerMessage.id,
        arrayIndex,
        presentation as AiChatbotTemplateTunerGeneration,
      );
    }
  }

  async sendMessage(userMessage: AIChatbotMessage): Promise<void> {
    if (!this.currentSession()) {
      this.startNewSession();
    }

    if (this.isProcessing) {
      throw new Error('Already processing a message');
    }

    const selectedPreview = this.selectedTemplateTunerPreview();
    this.isProcessing = true;
    this.interpretationResponse = undefined;

    const currentMessages = this.currentSession()!.messages();

    // If is resending a canceled message
    if (userMessage.isNotSuccessful) {
      userMessage.isNotSuccessful = false;

      // If it is not the last message, copy the message
      if (userMessage.id !== currentMessages[currentMessages.length - 1].id) {
        userMessage = cloneDeep(userMessage);
        userMessage.id = this.generateMessageId();
        this.updateMessages([...currentMessages, userMessage]);
      }
    } else {
      this.updateMessages([...currentMessages, userMessage]);
    }

    try {
      this.trackerService.presentationEvent(
        'AI Chatbot Message Sent',
        this.currentSession()!.id,
        userMessage,
      );

      const messageString = userMessage.content.message;
      let aiMessage: string;

      this.isLoadingTemplates = true;

      const actionStartedAt = Date.now();

      this.interpretationResponse = await this.aiChatbotApiService.interpret(
        this.currentSession()!,
        messageString,
        userMessage.content,
        this.cancelRequest$,
        selectedPreview ?? undefined,
        selectedPreview ? this.templateEditHistory : undefined,
        // Callback for SSE loading events (e.g., "generating", "editing")
        (event) => {
          this.sseLoadingMessage$.next(event);
          if (
            event.data === 'generating' &&
            this.selectedTemplateTunerPreview()
          ) {
            this.clearSelectedTemplateTunerPreview();
          }
        },
        // Callback for SSE partial template updates (as variations are generated)
        (partial, generationIndex) => {
          this.ssePartialTemplates$.next({ partial, generationIndex });
        },
        this.selectedMode(),
      );

      const actionDurationMs = Date.now() - actionStartedAt;

      this.isLoadingTemplates = false;

      if (this.interpretationResponse.type === 'chat') {
        aiMessage = this.interpretationResponse.data as unknown as string;
      } else if (this.interpretationResponse.type === 'explicit_material') {
        aiMessage =
          this.interpretationResponse.message ??
          "👀 Whoa there! Let's keep it clean.\n\nI can't generate anything explicit.";
      } else if (this.interpretationResponse.type === 'unsupported') {
        aiMessage =
          this.interpretationResponse.message ??
          "That one's outside my skillset (for now).";
      } else if (this.interpretationResponse.type === 'no_credits') {
        aiMessage =
          this.interpretationResponse.message ??
          'You have 0 credits remaining. Do you need more credits?';

        this.trackerService.presentationEvent(
          'AI Chatbot Ran Out of Credits',
          this.currentSession()?.id,
        );
      } else if (this.interpretationResponse.type === 'template_tuner_create') {
        aiMessage =
          this.interpretationResponse.message ??
          `Got it! Created three variations.`;

        this.trackerService.presentationEvent(
          'AI Chatbot Create Template',
          this.currentSession()?.id,
        );
      } else if (
        this.interpretationResponse.type === 'template_tuner_retrieve'
      ) {
        aiMessage =
          this.interpretationResponse.message ??
          `Got it! Retrieved three variations.`;
      } else if (this.interpretationResponse.type === 'template_tuner_select') {
        aiMessage =
          this.interpretationResponse.message ??
          `Got it! Showing option ${this.interpretationResponse.data.selectedIndex}.`;
      } else if (this.interpretationResponse.type === 'template_tuner_edit') {
        if (this.interpretationResponse.data.changesApplied) {
          aiMessage =
            this.interpretationResponse.message ?? `Got it! Applying edits.`;

          this.trackerService.presentationEvent(
            'AI Chatbot Edit Presentation',
            this.currentSession()?.id,
          );

          // Only update history and preview when an active preview exists
          if (this.selectedTemplateTunerPreview()) {
            // Push to edit history
            this.templateEditHistory.push({
              html: this.interpretationResponse.data.rawEditedContent,
              userMessage: messageString,
            });

            // Update the expanded preview with the new HTML content
            this.updateTemplateTunerPreviewContent(
              this.interpretationResponse.data.editedContent,
            );
          }
        } else {
          aiMessage =
            this.interpretationResponse.message ??
            `I couldn't apply those edits. Please try a different change.`;
        }
      } else if (this.interpretationResponse.type === 'show_branding') {
        this.showBranding.set(this.interpretationResponse.data);
        aiMessage =
          this.interpretationResponse.message ??
          (this.interpretationResponse.data
            ? 'Branding has been enabled.'
            : 'Branding has been disabled.');
      }

      if (aiMessage) {
        let messageContent;

        if (this.interpretationResponse.type === 'template_tuner_select') {
          messageContent = {
            type: 'message',
            message: aiMessage,
            data: aiMessage,
          };

          this.handleTemplateTunerSelection(
            this.interpretationResponse.data.selectedIndex,
          );
        } else if (this.interpretationResponse.type === 'template_tuner_edit') {
          messageContent = {
            type: 'message',
            message: aiMessage,
            data: aiMessage,
          };

          const editMessageIdx = this.currentSession()!.messages().length;

          this.conversationLoggingService
            .logEditTemplate(
              this.currentSession()!.id,
              editMessageIdx,
              messageString,
              this.interpretationResponse.data.editedContent,
              this.interpretationResponse.data.changesApplied,
              this.buildSelectedItem(),
              actionDurationMs,
              this.interpretationResponse.creditsUsed ?? 0,
            )
            .subscribe({
              error: (error) => {
                console.error(
                  'Error logging "Edit Template" event to BigQuery:',
                  error,
                );
              },
            });
        } else if (
          this.interpretationResponse.type === 'template_tuner_create'
        ) {
          this.clearSelectedTemplateTunerPreview();

          console.info(
            'Template Tuner variations',
            this.interpretationResponse.data,
          );

          messageContent = {
            type: 'template_tuner_create',
            message: aiMessage,
            data: this.interpretationResponse.data,
          };

          const createMessageIdx = this.currentSession()!.messages().length;
          const variations = this.interpretationResponse.data?.[0] ?? [];

          this.conversationLoggingService
            .logCreateTemplate(
              this.currentSession()!.id,
              createMessageIdx,
              variations,
              this.buildSelectedItem(),
              actionDurationMs,
              this.interpretationResponse.creditsUsed ?? 0,
            )
            .subscribe({
              error: (error) => {
                console.error(
                  'Error logging "Create Template" event to BigQuery:',
                  error,
                );
              },
            });
        } else if (
          this.interpretationResponse.type === 'template_tuner_retrieve'
        ) {
          this.clearSelectedTemplateTunerPreview();

          console.info(
            'Template Tuner variations',
            this.interpretationResponse.data,
          );

          messageContent = {
            type: 'template_tuner_create',
            message: aiMessage,
            data: this.interpretationResponse.data,
          };
        } else {
          messageContent = {
            type: 'message',
            message: aiMessage,
            data: aiMessage,
          };
        }

        const isTrackedAction =
          this.interpretationResponse.type === 'template_tuner_create' ||
          this.interpretationResponse.type === 'template_tuner_edit';

        this.addMessageAndRefresh(
          this.createAssistantMessage(messageContent),
          isTrackedAction
            ? {
                durationMs: actionDurationMs,
                creditsUsed: this.interpretationResponse.creditsUsed ?? 0,
              }
            : undefined,
        );
      }

      const creditsUsed = this.interpretationResponse.creditsUsed || 0;
      const noAvailableCredits =
        this.interpretationResponse.type === 'no_credits';
      const isEdit = this.interpretationResponse.type === 'template_tuner_edit';

      if (!isEdit && (creditsUsed > 0 || noAvailableCredits)) {
        this.aiCreditsService.showCreditsUsed(creditsUsed);
      }

      this.isProcessing = false;

      // Track AI response
      this.trackerService.presentationEvent(
        'AI Chatbot Response Received',
        this.currentSession()!.id,
      );
    } catch (error) {
      // Ensure UI returns to idle state on any error
      this.isProcessing = false;
      this.isLoadingTemplates = false;
      this.interpretationResponse = undefined;

      userMessage.isNotSuccessful = true;

      if (error === 'processing_canceled') {
        return;
      }

      const errorMessage: AIChatbotMessage = {
        id: this.generateMessageId(),
        timestamp: new Date(),
        role: 'error',

        content: {
          type: 'message',
          message:
            this.errorMessages[
              Math.floor(Math.random() * this.errorMessages.length)
            ],
        },
      };

      const currentMessages = this.currentSession()!.messages();
      this.updateMessages([...currentMessages, errorMessage]);

      this.trackerService.presentationEvent(
        'AI Chatbot Error',
        this.currentSession()!.id,
        error instanceof Error ? error.message : String(error),
      );

      throw error;
    }
  }

  addMessageAndRefresh(
    aiChatbotMessage?: AIChatbotMessage,
    loggingMeta?: { durationMs?: number; creditsUsed?: number },
  ) {
    const currentMessages = this.currentSession()!.messages();

    const newMessages = [
      ...currentMessages,
      ...(aiChatbotMessage ? [aiChatbotMessage] : []),
    ];

    this.updateMessages(newMessages, loggingMeta);
  }

  stopProcessing(): void {
    this.isProcessing = false;
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
    this.cancelRequest$ = new Subject<void>();
  }

  clearSession(): void {
    const session = this.currentSession();
    if (session) {
      this.trackerService.presentationEvent(
        'AI Chatbot Session Cleared',
        session.id,
      );
    }

    this.stopProcessing();
    this.clearSelectedTemplateTunerPreview();
    this.interpretationResponse = undefined;
    this.currentSession.set(undefined);
    this.scrollChatToBottom$.next({});
    this.aiCreditsService.closeOverlay();
  }

  restartSession(): void {
    this.clearSession();
    this.startNewSession();
  }

  private generateSessionId(): string {
    return (
      'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    );
  }

  generateMessageId(): string {
    return 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  createAssistantMessage(content: AIChatbotMessageContent): AIChatbotMessage {
    return {
      id: this.generateMessageId(),
      timestamp: new Date(),
      role: 'assistant',
      content,
    };
  }

  createAssistantTextMessage(message: string): AIChatbotMessage {
    return this.createAssistantMessage({
      type: 'message',
      message,
      data: message,
    });
  }

  async createPresentationFromTemplateTuner(
    templateTuner: AiChatbotTemplateTunerGeneration,
  ): Promise<void> {
    try {
      const presentationName = templateTuner.title ?? templateTuner.prompt;

      // Validate content is available from server
      if (!templateTuner.content) {
        console.error(
          'content is not available. Server needs to provide content field.',
        );
        throw new Error(
          'Template content is not yet available. Please try again later.',
        );
      }

      const session = this.currentSession();

      // Log "Open in Editor" event to BigQuery
      if (session) {
        const templateHtml = JSON.stringify({
          prompt: templateTuner.prompt,
          style: templateTuner.style,
          content: templateTuner.content,
          rawContent: templateTuner.rawContent,
          rawContentUrl: templateTuner.rawContentUrl,
        });

        const preview = this.selectedTemplateTunerPreview();
        let idx = preview
          ? session.messages().findIndex((m) => m.id === preview.messageId)
          : -1;

        // If template was modified, use the last message index
        if (idx >= 0 && this.templateEditHistory.length > 0) {
          idx = session.messages().length - 1;
        }

        this.conversationLoggingService
          .logOpenInEditor(
            session.id,
            templateHtml,
            idx >= 0 ? idx : undefined,
            this.buildSelectedItem(),
          )
          .subscribe({
            error: (error) => {
              console.error(
                'Error logging "Open in Editor" event to BigQuery:',
                error,
              );
            },
          });
      }

      this.trackerService.presentationEvent(
        'Template Tuner Presentation Created',
        this.currentSession()?.id,
        presentationName,
        { source: 'template-tuner' },
      );

      this.trackerService.presentationEvent(
        'AI Chatbot Open in Editor',
        this.currentSession()?.id,
      );

      // Prevent addFromLayout from navigating with location: 'replace',
      this.templateEditorService.aiEditorActive = true;

      // Create presentation with layout directly
      const presentationId = await this.templateEditorService.addFromLayout(
        presentationName,
        templateTuner.content,
        { source: 'template-tuner' },
        undefined, // folderId
        !this.showBranding()
          ? (AIChatbotService.extractBrandingColors(templateTuner.content) ??
              undefined)
          : undefined,
        templateTuner.orientation,
      );

      this.templateEditorService.aiEditorActive = false;

      // Close all modals
      this.dialog.closeAll();

      // Navigate to the editor without replacing /chat in history,
      this.stateService.go('apps.editor.templates.edit', {
        presentationId,
        productId: undefined,
      });
    } catch (error) {
      console.error('Error creating presentation from template tuner:', error);
      throw error;
    }
  }
}
